export * from './integration-configs.js';
export * from './workflows.js';

import { z } from 'zod';
import { integrationProviderSchema } from './integration-configs.js';

export const paginationSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  pageSize: z.coerce.number().min(1).max(100).default(20),
});

export const dateRangeSchema = z.object({
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
});

export const sortSchema = z.object({
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
});

// Strong password requirements for security
export const strongPasswordSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters')
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^()_+\-=\[\]{}|;:'"<>,.\/\\])/,
    'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
  );

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: strongPasswordSchema,
  name: z.string().min(1, 'Name is required').max(100),
});

export const refreshTokenSchema = z.object({
  refreshToken: z.string().min(1, 'Refresh token is required'),
});

export const twoFactorSetupVerifySchema = z.object({
  token: z.string().length(6, 'Token must be 6 digits').regex(/^\d+$/, 'Token must be numeric'),
  secret: z.string().min(16, 'Invalid secret'),
});

export const twoFactorVerifySchema = z.object({
  challengeToken: z.string().uuid('Invalid challenge token'),
  token: z.string().length(6, 'Token must be 6 digits').regex(/^\d+$/, 'Token must be numeric'),
});

export const twoFactorRecoverySchema = z.object({
  challengeToken: z.string().uuid('Invalid challenge token'),
  recoveryCode: z.string().regex(/^[A-Z0-9]{5}-[A-Z0-9]{5}$/i, 'Invalid recovery code format'),
});

export const twoFactorDisableSchema = z.object({
  password: z.string().min(1, 'Password is required'),
  token: z.string().length(6, 'Token must be 6 digits').regex(/^\d+$/, 'Token must be numeric'),
});

export const createIntegrationSchema = z.object({
  provider: integrationProviderSchema,
  name: z.string().min(1, 'Name is required').max(100),
  config: z.record(z.unknown()),
});

export const updateIntegrationSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  config: z.record(z.unknown()).optional(),
  enabled: z.boolean().optional(),
});

export const triggerSyncSchema = z.object({
  entities: z.array(z.string()).optional(),
  fullSync: z.boolean().default(false),
});

export const eventsQuerySchema = paginationSchema.merge(dateRangeSchema).merge(sortSchema).extend({
  source: z.string().optional(),
  category: z.enum(['sync', 'webhook', 'workflow', 'auth', 'integration', 'error', 'system']).optional(),
  severity: z.enum(['info', 'warning', 'error', 'critical']).optional(),
  search: z.string().optional(),
});

// Entity source enum matching the database enum
export const entitySourceSchema = z.enum([
  'attio',
  'odoo',
  'notion',
  'sequesto_app',
  'manual',
]);

export const syncedDataQuerySchema = paginationSchema.merge(dateRangeSchema).merge(sortSchema).extend({
  search: z.string().optional(),
  source: entitySourceSchema.optional(),
  ids: z.array(z.string()).optional(),
});

export const companiesQuerySchema = syncedDataQuerySchema.extend({
  stage: z.enum(['lead', 'prospect', 'qualified', 'customer', 'churned', 'archived']).optional(),
  funnelStage: z.enum(['visitor', 'lead', 'mql', 'sal', 'sql', 'customer']).optional(),
  leadChannel: z.enum(['paid', 'organic', 'direct', 'outbound']).optional(),
  industry: z.string().optional(),
  country: z.string().optional(),
});

export const dealsQuerySchema = syncedDataQuerySchema.extend({
  stage: z.enum(['discovery', 'qualification', 'proposal', 'negotiation', 'closed_won', 'closed_lost']).optional(),
  companyId: z.string().optional(),
  minValue: z.coerce.number().optional(),
  maxValue: z.coerce.number().optional(),
});

export const invoicesQuerySchema = syncedDataQuerySchema.extend({
  status: z.enum(['draft', 'sent', 'paid', 'overdue', 'cancelled', 'refunded']).optional(),
  companyId: z.string().optional(),
});

// Valid date formats: YYYY-MM-DD, XdaysAgo, today, yesterday
const dateStringSchema = z
  .string()
  .regex(
    /^(\d{4}-\d{2}-\d{2}|\d+daysAgo|today|yesterday)$/,
    'Date must be YYYY-MM-DD, XdaysAgo, today, or yesterday'
  );

export const funnelQuerySchema = z.object({
  startDate: dateStringSchema.optional(),
  endDate: dateStringSchema.optional(),
  channel: z.enum(['paid', 'paid_google', 'paid_linkedin', 'paid_meta', 'organic', 'direct', 'outbound', 'unknown']).optional(),
  funnelType: z.enum(['website', 'outbound', 'inbound']).optional(),
});

export const updateFunnelStageSchema = z.object({
  funnelStage: z.enum(['visitor', 'lead', 'mql', 'sal', 'sql', 'customer']),
});

export type FunnelQueryInput = z.infer<typeof funnelQuerySchema>;
export type UpdateFunnelStageInput = z.infer<typeof updateFunnelStageSchema>;
export type PaginationInput = z.infer<typeof paginationSchema>;
export type DateRangeInput = z.infer<typeof dateRangeSchema>;
export type SortInput = z.infer<typeof sortSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type RefreshTokenInput = z.infer<typeof refreshTokenSchema>;
export type CreateIntegrationInput = z.infer<typeof createIntegrationSchema>;
export type UpdateIntegrationInput = z.infer<typeof updateIntegrationSchema>;
export type TriggerSyncInput = z.infer<typeof triggerSyncSchema>;
export type EventsQueryInput = z.infer<typeof eventsQuerySchema>;
export type SyncedDataQueryInput = z.infer<typeof syncedDataQuerySchema>;
export type CompaniesQueryInput = z.infer<typeof companiesQuerySchema>;
export type DealsQueryInput = z.infer<typeof dealsQuerySchema>;
export type InvoicesQueryInput = z.infer<typeof invoicesQuerySchema>;
export type TwoFactorSetupVerifyInput = z.infer<typeof twoFactorSetupVerifySchema>;
export type TwoFactorVerifyInput = z.infer<typeof twoFactorVerifySchema>;
export type TwoFactorRecoveryInput = z.infer<typeof twoFactorRecoverySchema>;
export type TwoFactorDisableInput = z.infer<typeof twoFactorDisableSchema>;
